import {di, lazyGet} from "@dp-instant/core";
import {Entity, EntitySnapshot, IterativeSystem, ReadonlyEntity} from "tick-knock";
import {FighterView} from "@game/coregame/view/FighterView";
import {Fighter} from "@game/coregame/ecs/entities/Fighter";
import {PCell} from "@game/coregame/ecs/entities/PCell";
import {FighterDeathEffectAction} from "@game/coregame/actions/FighterDeathAction";
import {CapitalView} from "@game/coregame/view/CapitalView";
import {TypesGame} from "@game/TypesGame";
import {Container} from "pixi.js-legacy";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {hapticBattle} from "@game/haptic";

export class FighterEvent {
    static readonly FIGHTER_CREATED = "FighterCreated";
    static readonly FIGHTER_DIED = "FighterDied";
    constructor(public readonly type: string, public readonly fighter: Fighter) {}
}
export class FighterMovementSystem extends IterativeSystem {
    private cells: Map<string, PCell> = new Map<string, PCell>(); // TODO: use int key and array instead

    constructor() {
        super(entity => entity.has(FighterView));
    }

    public update(dt: number) {
        super.update(dt);
        this.checkCollisions();
    }
    private checkCollisions() {
        this.cells.forEach(cell => {
            const fighters = cell.getCollisions();
            fighters.forEach(collision => {
                if (collision.some(f => f.owner === PlayerType.First)) hapticBattle();
                di.get<FighterDeathEffectAction>(FighterDeathEffectAction).run(collision);
                collision.forEach(fighter => {
                    if (!fighter.hasTag(Fighter.TAG_DIED)) {
                        fighter.addTag(Fighter.TAG_DIED);
                        fighter.group?.OnFighterDied();
                    }
                    this.engine.removeEntity(fighter);
                });
            });
        });
    }
    protected updateEntity(fighter: Fighter, dt: number) {
        const isPathFinished = fighter.move(dt);
        if (isPathFinished) {
            fighter.target.tryOccupy(fighter);
            this.engine.removeEntity(fighter);
        } else {
            const [i, j] = fighter.position.map(x => Math.round(x / PCell.SIZE)); // found position on grid
            const id = `${i}_${j}`;
            const cell = this.cells.get(id) || new PCell();
            cell.add(fighter);
            this.cells.set(id, cell);
        }
        if (fighter.has(FighterView)) {
            const view = fighter.get(FighterView)!;
            view.position.set(...fighter.position);
            view.rotation = fighter.rotation;
        }
    }
    protected entityAdded = (snapshot: EntitySnapshot) => {
        const fighter = snapshot.current as Fighter;
        this.addView(fighter);
        this.dispatch(new FighterEvent(FighterEvent.FIGHTER_CREATED, fighter));
    };

    protected entityRemoved = (snapshot: EntitySnapshot) => {
        const {current: entity} = snapshot;
        this.removeView(entity);
        this.dispatch(new FighterEvent(FighterEvent.FIGHTER_DIED, entity as Fighter));
    };
    private addView(fighter: Fighter) {
        const view = fighter.get(FighterView);
        const sourceCapital = fighter.source.get(CapitalView)!;

        const container = lazyGet<{fighters: Container}>(TypesGame.views.fieldInstance)?.fighters;
        if (view && container) {
            const {x, y} = (view.position = container.toLocal(
                sourceCapital.position,
                sourceCapital.parent
            ));
            fighter.position[0] = x;
            fighter.position[1] = y;
            container.addChild(view);
        }
    }
    private removeView(entity: Entity | ReadonlyEntity) {
        const view = entity.get(FighterView);
        view?.parent?.removeChild(view);
    }

    onRemovedFromEngine() {
        this.cells.clear();
        let len = this.engine.entities.length;
        while (len) {
            if (this.entities[0]) {
                this.engine.removeEntity(this.entities[0]);
            }
            len--;
        }
        super.onRemovedFromEngine();
    }
}
